import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';

import { useGetTimeToSellQuery } from 'services/api/cars.api';

interface SoldCarsAnalysisProps {
  businessUnitId: number;
  onClose: () => void;
}

const SoldCarsAnalysis: React.FC<SoldCarsAnalysisProps> = ({ businessUnitId, onClose }) => {
  const { data, error, isLoading } = useGetTimeToSellQuery({ businessUnitId });

  const getChartData = () => {
    if (!data) return { labels: [], values: [] };
    return data.buckets;
  };

  const getModelDistributionData = () => {
    if (!data) return [];
    return data.modelStatistics
      .slice(0, 10) // Top 10 models
      .map((model) => ({
        id: model.modelKey,
        label: model.modelName || `Model ${model.modelKey}`,
        value: model.totalCars,
      }));
  };

  const getDailyChartData = () => {
    if (!data || !data.dailyBuckets) return { labels: [], values: [] };
    const sortedDays = Object.entries(data.dailyBuckets)
      .map(([day, count]) => ({ day: parseInt(day, 10), count }))
      .sort((a, b) => a.day - b.day);
    return {
      labels: sortedDays.map((item) => `${item.day} día${item.day !== 1 ? 's' : ''}`),
      values: sortedDays.map((item) => item.count),
    };
  };

  const getSpeedCategories = () => {
    if (!data) return { fast: [], medium: [], slow: [] };
    const sortedBySpeed = [...data.modelStatistics].sort((a, b) => a.percentile10 - b.percentile10);
    return {
      fast: sortedBySpeed.slice(0, 3),
      medium: sortedBySpeed.slice(3, -3),
      slow: sortedBySpeed.slice(-3),
    };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error: {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No data available
      </Alert>
    );
  }

  const chartData = getChartData();
  const modelDistribution = getModelDistributionData();
  const speedCategories = getSpeedCategories();
  const dailyChartData = getDailyChartData();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Análisis de Autos Vendidos</Typography>
        <Button onClick={onClose}>Cerrar</Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Autos Vendidos
              </Typography>
              <Typography variant="h4">{data.totalSoldCars}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Tiempo Promedio de Venta
              </Typography>
              <Typography variant="h4">{data.averageTimeToSell} días</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Modelos Únicos Analizados
              </Typography>
              <Typography variant="h4">{data.modelStatistics.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Time to Sell Distribution Chart */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Tiempo en vender los autos (quitarlos del stock)
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Agrupado por intervalo de días
        </Typography>
        <Box sx={{ height: 400 }}>
          <BarChart
            xAxis={[
              {
                scaleType: 'band',
                data: chartData.labels,
              },
            ]}
            series={[
              {
                data: chartData.values,
                color: '#1976d2',
              },
            ]}
            height={350}
          />
        </Box>
      </Paper>

      {/* Daily Buckets Chart */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Distribución de ventas por día
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Cantidad de autos vendidos por día específico
        </Typography>
        <Box sx={{ height: 400 }}>
          <BarChart
            xAxis={[
              {
                scaleType: 'band',
                data: dailyChartData.labels,
              },
            ]}
            series={[
              {
                data: dailyChartData.values,
                color: '#2e7d32',
              },
            ]}
            height={350}
          />
        </Box>
      </Paper>

      {/* Model Distribution */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">Top 10 modelos más vendidos</Typography>
            <Box sx={{ height: 300 }}>
              <BarChart
                xAxis={[
                  {
                    scaleType: 'band',
                    data: modelDistribution.map((item) => item.label),
                  },
                ]}
                series={[
                  {
                    data: modelDistribution.map((item) => item.value),
                    color: '#1976d2',
                  },
                ]}
                height={280}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Models by Speed Category */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Modelos por velocidad de venta
        </Typography>

        {/* Fast Selling Models */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            🚀 Venta Rápida (Mejores modelos)
          </Typography>
          <Grid container spacing={2}>
            {speedCategories.fast.map((model) => (
              <Grid item xs={12} md={4} key={model.modelKey}>
                <Card variant="outlined" sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {model.modelName || `Model ${model.modelKey}`} ({model.totalCars} autos)
                    </Typography>
                    <Typography variant="body2">Promedio: {model.averageTimeToSell} días</Typography>
                    <Typography variant="body2">10% más rápido: {model.percentile10} días</Typography>
                    <Typography variant="body2">10% más lento: {model.percentile90} días</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Slow Selling Models */}
        <Box>
          <Typography variant="h6" color="warning.main" gutterBottom>
            🐌 Venta Lenta (Modelos que requieren atención)
          </Typography>
          <Grid container spacing={2}>
            {speedCategories.slow.map((model) => (
              <Grid item xs={12} md={4} key={model.modelKey}>
                <Card variant="outlined" sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {model.modelName || `Model ${model.modelKey}`} ({model.totalCars} autos)
                    </Typography>
                    <Typography variant="body2">Promedio: {model.averageTimeToSell} días</Typography>
                    <Typography variant="body2">10% más rápido: {model.percentile10} días</Typography>
                    <Typography variant="body2">10% más lento: {model.percentile90} días</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      {/* Detailed Model Statistics Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Estadísticas Detalladas por Modelo
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ p: 1, textAlign: 'left', borderBottom: 1, borderColor: 'divider' }}>
                  Modelo
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  Total Autos
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  Promedio
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  10% Más Rápido
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  10% Más Lento
                </Box>
                <Box component="th" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  Rango
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {data.modelStatistics.map((model) => (
                <Box component="tr" key={model.modelKey}>
                  <Box component="td" sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {model.modelName || `Model ${model.modelKey}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    {model.totalCars}
                  </Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    {model.averageTimeToSell} días
                  </Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    {model.percentile10} días
                  </Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    {model.percentile90} días
                  </Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    {model.minTimeToSell} - {model.maxTimeToSell} días
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SoldCarsAnalysis;
